import dayjs from 'dayjs';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { EstateTaxFilingStatus } from '/@/modules/estatetaxfiling/enums/EstateTaxFilingStatus';
import NumeralService from '/@/modules/core/app/services/NumeralService';
import { ColumnFormat } from '/@/modules/core/app/constants/data.constants';
import { DateFormat } from '/@/modules/core/app/constants/date.constants';
import { OwnedTaxType } from '/@/modules/tax/constants/OwnedTaxType';
import { DocumentSubType } from '/@/modules/document/enums/DocumentSubType';
import { EstateTaxFormType } from '@/modules/estatetaxform/enums/EstateTaxFormType';

export class EstateTaxFiling extends BaseEntityModel {
  /**
   * @var {number}
   */
  id;

  /**
   * @var {number}
   */
  estateId;

  /**
   * @var {number}
   */
  year;

  /**
   * @var {boolean}
   */
  hasTransaction;

  /**
   * @var {string}
   */
  status;

  /**
   * @var {string}
   */
  ein;

  /**
   * @relationship
   * @var {EstateTaxForm[]}
   */
  estateTaxForms;

  /**
   * @relationship
   * @var {Asset}
   */
  asset;

  /**
   * @relationship
   * @var {Liability}
   */
  liability;

  constructor(model = {}) {
    super(model);
    super.setRelationships(this, model, this.getModelName());

    this.id = model.id;
    this.estateId = model.estateId;
    this.year = model.year;
    this.hasTransaction = model.hasTransaction;
    this.status = model.status;
    this.ein = model.ein;
  }

  /**
   *
   * @returns {string}
   */
  getModelName() {
    return ModelType.ESTATE_TAX_FILING;
  }

  /**
   * @returns {EstateTaxForm}
   */
  get t1TaxForm() {
    return this.estateTaxForms.find((form) => form.type === EstateTaxFormType.T1.key);
  }

  /**
   * @returns {EstateTaxForm}
   */
  get t183TaxForm() {
    return this.estateTaxForms.find((form) => form.type === DocumentSubType.TAX_T183.key);
  }

  /**
   * @returns {boolean}
   */
  get isIncomeTaxAndBenefitReturnFormAccepted() {
    return this.t1TaxForm.isAccepted || this.t183TaxForm.isAccepted;
  }

  /**
   * @returns {boolean}
   */
  get isIncomeTaxAndBenefitReturnFormSubmittedToCraOrAccepted() {
    return (
      this.t1TaxForm.isAccepted ||
      this.t1TaxForm.isSubmittedToCRA ||
      this.t183TaxForm.isAccepted ||
      this.t183TaxForm.isSubmittedToCRA
    );
  }

  /**
   * @returns {boolean}
   */
  get areAllTaxFormsAccepted() {
    return this.estateTaxForms?.every((taxForm) => {
      if (DocumentSubType.isIncomeTaxAndBenefitReturnTaxFormTypeTax(taxForm.type)) {
        return this.isIncomeTaxAndBenefitReturnFormAccepted;
      }

      return taxForm.isAccepted || taxForm.isNotApplicable;
    });
  }

  /**
   * @returns {boolean}
   */
  get areAllTaxFormsSubmittedToCraOrAccepted() {
    return this.estateTaxForms?.every((taxForm) => {
      if (DocumentSubType.isIncomeTaxAndBenefitReturnTaxFormTypeTax(taxForm.type)) {
        return this.isIncomeTaxAndBenefitReturnFormSubmittedToCraOrAccepted;
      }

      return taxForm.isSubmittedToCRA || taxForm.isAccepted || taxForm.isNotApplicable;
    });
  }

  /**
   * @returns {boolean}
   */
  get isNew() {
    return this.status === EstateTaxFilingStatus.NEW.key;
  }

  /**
   * @returns {boolean}
   */
  get isSubmitted() {
    return this.status === EstateTaxFilingStatus.SUBMITTED.key;
  }

  /**
   * @returns {boolean}
   */
  get isConfirmed() {
    return this.status === EstateTaxFilingStatus.CONFIRMED.key;
  }

  /**
   * @returns {boolean}
   */
  get isRefunded() {
    return this.status === EstateTaxFilingStatus.REFUNDED.key;
  }

  /**
   * @returns {boolean}
   */
  get isPaid() {
    return this.status === EstateTaxFilingStatus.PAID.key;
  }

  /**
   * @returns {{}|EstateTaxFilingStatus}
   */
  get statusEnum() {
    return EstateTaxFilingStatus.getByKey(this.status);
  }

  /**
   * @returns {string}
   */
  get dueDateDisplay() {
    return `Due April 31st, ${this.year + 1}`;
  }

  /**
   * @returns {boolean}
   */
  get isPayable() {
    return !!this.liability;
  }

  /**
   * @returns {boolean}
   */
  get isRefund() {
    return !!this.asset;
  }

  /**
   * @returns {?string}
   */
  get noticeOfAssessmentDocumentUpdatedAt() {
    return dayjs(this.documents.noticeOfAssessmentDocument?.updatedAt).format(DateFormat.ISO);
  }

  /**
   * @returns {?string}
   */
  get taxOwingVoucherDocumentUpdatedAt() {
    return dayjs(this.documents.taxOwingVoucherDocument?.updatedAt).format(DateFormat.ISO);
  }

  /**
   * @returns {?string}
   */
  get amountDisplay() {
    if (this.asset) {
      return NumeralService.formatValue(this.asset.currentValue, ColumnFormat.FORMAT_CURRENCY, 2);
    }
    if (this.liability) {
      return NumeralService.formatValue(
        this.liability.displayAmount,
        ColumnFormat.FORMAT_CURRENCY,
        2
      );
    }
    return null;
  }

  /**
   * @returns {number}
   */
  get amount() {
    return this.asset ? this.asset.currentValue ?? 0 : this.liability?.currentBalanceAmount ?? 0;
  }

  /**
   * @returns {string}
   */
  get taxType() {
    return this.asset ? OwnedTaxType.REFUND.key : OwnedTaxType.PAYABLE.key;
  }

  /**
   *
   * @returns {Asset|Liability|null}
   */
  get taxModel() {
    return this.liability ?? this.asset ?? null;
  }

  confirmTaxAmountOwing() {
    this.status = EstateTaxFilingStatus.CONFIRMED.key;

    if (this.taxType === OwnedTaxType.REFUND.key) {
      this.asset.meta.isConfirmed = true;
    }

    if (this.taxType === OwnedTaxType.PAYABLE.key) {
      this.liability.meta.isConfirmed = true;
    }
  }

  /**
   * @returns {boolean}
   */
  ownedTaxAmountNotSubmitted() {
    return (
      [EstateTaxFilingStatus.NEW.key, EstateTaxFilingStatus.SUBMITTED.key].includes(this.status) &&
      this.amount === 0
    );
  }
}
